import { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  Badge,
  Typography,
  Space,
  Input,
  Modal,
  message,
} from "antd";
import { PlusOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import { useNavigate, NavLink } from "react-router-dom";
import moment from "moment";

const { Title } = Typography;

export const DynamicRiskAssessmentList = () => {
  const [dras, setDras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const fetchDras = async (page = 1, pageSize = 10, search = "") => {
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      setDras([]);
      setPagination({
        current: page,
        pageSize,
        total: 0,
      });
      console.log(search); // to suppress unused search var
    } catch (error) {
      message.error("Failed to load Dynamic Risk Assessments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDras();
  }, []);

  const handleTableChange = (newPagination: any) => {
    fetchDras(newPagination.current, newPagination.pageSize, searchQuery);
  };

  const handleSearch = (value: any) => {
    setSearchQuery(value);
    fetchDras(1, pagination.pageSize, value);
  };

  const handleDelete = (id: any) => {
    Modal.confirm({
      title: "Are you sure you want to delete this DRA?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          // MOCK DELETE await api.delete(`/daily-ops/dra/${id}`);
          console.log(id); // suppress unused id warning
          await new Promise((r) => setTimeout(r, 300));
          message.success("DRA deleted successfully");
          fetchDras(pagination.current, pagination.pageSize, searchQuery);
        } catch (error) {
          message.error("Failed to delete DRA");
        }
      },
    });
  };

  const columns = [
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Decision",
      dataIndex: "decision",
      key: "decision",
      render: (decision: any) => {
        let color: "default" | "success" | "warning" | "error" | "processing" =
          "default";
        let text = "Unknown";
        if (decision === 1) {
          color = "success";
          text = "Continue Work";
        }
        if (decision === 2) {
          color = "warning";
          text = "Modify Work";
        }
        if (decision === 3) {
          color = "error";
          text = "Stop Work";
        }
        return <Badge status={color} text={text} />;
      },
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: any) => moment(date).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/daily-ops/dra/${record.id}`)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Title level={4}>Dynamic Risk Assessments</Title>
        <Space>
          <Input.Search
            placeholder="Search DRAs"
            allowClear
            onSearch={handleSearch}
            style={{ width: 250 }}
          />
          <NavLink
            to="/daily-ops/dra/new"
            className="flex items-center gap-2 rounded-2xl bg-secondary-500 px-4 py-2 text-sm shrink-0"
          >
            <PlusOutlined />
            <span>Create</span>
          </NavLink>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={dras}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
      />
    </Card>
  );
};
